import { AxiosError } from 'axios'
import isLogoutUrl from './isLogoutUrl'

/** Routes on which a 401 is expected and must not trigger a logout — you are already signed out. */
const UNAUTHENTICATED_ROUTES = ['/login', '/register']

/** Decides, from a request URL, whether a 401 belongs to something other than the Tardek session. */
export type UrlPredicate = (url: string | undefined) => boolean

/**
 * Whether a 401 should end the whole Tardek session.
 *
 * Pure, and separate from the effects below, because the interesting part is the decision. Two
 * reasons to say no:
 *
 *  - We are already on a page that has no session to end.
 *  - `isExempt` says the 401 came from somewhere whose authentication is not this session's. In
 *    practice that is the **copilot**, a separately deployed service: an expired or unconfirmable
 *    operator token there, its outbound confirmation hop failing, or — the case that has actually
 *    happened — an ordinary bug surfacing as `401 "invalid authorization token"` because the service
 *    maps every `ValueError` to that status. None of those means the operator's Tardek session is
 *    over, and treating them as if it were discards an in-progress declaration.
 *
 * The predicate is INJECTED rather than imported: this module is in `shared/`, which features import
 * from and not the other way round, and `features/copilot` already imports from `shared/` — so
 * reaching back into it here would close a require cycle at the first import in the other direction.
 */
export function shouldLogoutOn401(url: string | undefined, pathname: string, isExempt: UrlPredicate): boolean {
  if (UNAUTHENTICATED_ROUTES.includes(pathname)) return false
  if (isLogoutUrl(url)) return false
  return !isExempt(url)
}

/** What ending the session actually does. Injected so the decision stays testable without stubbing
 *  authentication or navigation. */
export interface UnauthorizedEffects {
  /** Cancel in-flight requests, so none of them lands after the session is gone. */
  abort: () => void
  /** End the session: clear credentials and leave for the login page. */
  logout: () => void
}

/**
 * The rejection half of the global response interceptor. Always re-rejects — it decides whether to
 * end the session on the way past, and never swallows the error.
 */
export function unauthorizedInterceptor(effects: UnauthorizedEffects, isExempt: UrlPredicate) {
  return (error: AxiosError) => {
    if (error?.response?.status === 401
      && shouldLogoutOn401(error.config?.url, window.location.pathname, isExempt)) {
      effects.abort()
      effects.logout()
    }
    return Promise.reject(error)
  }
}
